/**
 * One labeled, auto-saving learned-insight field on the agent-guidance
 * settings page.
 *
 * Behavior mirrors `EditableDefinitionRow` inside the welcome proposal:
 *   - local draft, auto-saves on blur, reverts on rejected save
 *   - disabled while the server value is the "Analyzing..." sentinel
 *   - textarea auto-grows to fit content (no scrollbar / resize handle)
 *
 * Visual style is settings-page boxed (ring + shadow) instead of inline,
 * because users land here from a settings nav and the fields should look
 * like proper form inputs.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { InsightItem } from '@/hooks/profile/useLearnedInsights';
import {
  ANALYZING_PLACEHOLDER,
  ANALYZING_PLACEHOLDER_DISPLAY,
} from '@/lib/proposals/analyzingPlaceholder';
import { cx } from '@/utils/cx';

const FIELD_CLASSES =
  'mt-1 w-full rounded-lg bg-[var(--color-bg-primary)] px-3 py-2 text-sm ' +
  'text-[var(--color-text-primary)] ring-1 ring-[var(--color-border-primary)] ring-inset shadow-xs ' +
  'placeholder:text-[var(--color-text-placeholder)] ' +
  'focus:ring-2 focus:ring-[var(--color-brand-v2-primary)] focus:outline-hidden ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--color-bg-disabled-subtle)] ' +
  'disabled:ring-[var(--color-border-disabled)] disabled:text-[var(--color-text-disabled)]';

interface LearnedInsightFieldProps {
  item: InsightItem;
  /** Resolves on persistence; rejects to signal the row should revert. */
  onSave: (term: string, value: string) => Promise<void>;
  /**
   * Initial fetch hasn't resolved yet. We disable the field but still render
   * the label and a placeholder so the section doesn't reflow when the GET
   * arrives.
   */
  isInitialLoading?: boolean;
}

export function LearnedInsightField({
  item,
  onSave,
  isInitialLoading = false,
}: LearnedInsightFieldProps) {
  const isAnalyzing = item.definition === ANALYZING_PLACEHOLDER;
  const initialValue = isAnalyzing ? '' : item.definition;

  const [draft, setDraft] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const lastServerValueRef = useRef(item.definition);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resync the local draft when the server pushes a new value — but only if
  // the user hasn't drifted off the previous server value. Treat any draft
  // that differs from the prior server value as a user edit (including an
  // intentional empty clear).
  useEffect(() => {
    if (item.definition === lastServerValueRef.current) {
      return;
    }
    const previousServerValue =
      lastServerValueRef.current === ANALYZING_PLACEHOLDER
        ? ''
        : lastServerValueRef.current;
    const userHasDrafted = draft !== previousServerValue;
    if (!userHasDrafted) {
      setDraft(
        item.definition === ANALYZING_PLACEHOLDER ? '' : item.definition
      );
    }
    lastServerValueRef.current = item.definition;
  }, [item.definition, draft]);

  // Auto-grow the textarea to fit its content (no internal scrollbar). Width
  // comes from the parent so scrollHeight is meaningful as soon as the
  // element is in the DOM. Mirrors the welcome proposal's auto-resize.
  useLayoutEffect(() => {
    if (!item.multiline) {
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft, item.multiline]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDraft(e.target.value);
    },
    []
  );

  const handleBlur = useCallback(async () => {
    const serverValue =
      lastServerValueRef.current === ANALYZING_PLACEHOLDER
        ? ''
        : lastServerValueRef.current;
    if (draft === serverValue) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave(item.term, draft);
      lastServerValueRef.current = draft;
    } catch {
      // Parent surfaces the toast; revert so the row reflects what's
      // actually persisted server-side.
      setDraft(serverValue);
    } finally {
      setIsSaving(false);
    }
  }, [draft, item.term, onSave]);

  // Enter commits a single-line input (matches native form UX). Ctrl/Cmd+Enter
  // commits a textarea so users can still insert newlines with plain Enter.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const isTextArea = e.currentTarget.tagName === 'TEXTAREA';
      const wantsCommit =
        e.key === 'Enter' && (isTextArea ? e.metaKey || e.ctrlKey : true);
      if (wantsCommit) {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
    []
  );

  const isDisabled = isSaving || isAnalyzing || isInitialLoading;
  let placeholder = '';
  if (isAnalyzing) {
    placeholder = ANALYZING_PLACEHOLDER_DISPLAY;
  } else if (isInitialLoading) {
    placeholder = 'Loading…';
  }
  const fieldId = `learned-insight-${item.term.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div>
      <label
        htmlFor={fieldId}
        className='block text-sm font-medium text-fg-primary'
      >
        {item.term}
      </label>
      {item.multiline ? (
        <textarea
          ref={textareaRef}
          id={fieldId}
          aria-label={item.term}
          value={draft}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          rows={1}
          className={cx(FIELD_CLASSES, 'resize-none overflow-hidden')}
        />
      ) : (
        <input
          id={fieldId}
          type='text'
          aria-label={item.term}
          value={draft}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          className={FIELD_CLASSES}
        />
      )}
    </div>
  );
}
